import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Switch,
  TextField,
  Button,
  Divider,
  IconButton,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputAdornment,
} from "@mui/material";
import ArrowBackIosNewOutlined from "@mui/icons-material/ArrowBackIosNewOutlined";
import LockIcon from "@mui/icons-material/Lock";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { baseapi } from "../Environmentalurl";
import {
  ApplicationType,
  entity_id,
  getGeolocation,
  location,
  Platform,
} from "../Utils/Const";
import useIPAddress from "../Utils/useIPAddress";
import DashboardHeader from "../Dashboard/DashboardHeader";
import api from "../Utils/Api";

export default function WalletUsageLimits() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const pageBg = isDark ? "#0B1321" : "#eef2f5";
  const cardBg = isDark ? "#162033" : "#ffffff";
  const innerCardBg = isDark ? "#0D1729" : "#f8f9fb";
  const borderColor = isDark
    ? "1px solid rgba(90,126,173,0.40)"
    : "1px solid rgba(0,0,0,0.12)";
  const textColor = isDark ? "#ffffff" : "#0e1b2c";
  const labelColor = isDark ? "#b8c4d6" : "#516070";
  const [isEnabled, setIsEnabled] = useState(false);
  const [transactionLimit, setTransactionLimit] = useState("");
  const [amountLimit, setAmountLimit] = useState("");
  const [showUpdateFields, setShowUpdateFields] = useState(false);
  const [newTransactionLimit, setNewTransactionLimit] = useState("");
  const [newAmountLimit, setNewAmountLimit] = useState("");

  const [dialog, setDialog] = useState({
    open: false,
    title: "",
    message: "",
    isSuccess: true,
  });

  const ip = useIPAddress();

  const amountFromSession = sessionStorage.getItem("DailyAmount");
  const countFromSession = sessionStorage.getItem("DailyCount");

  // ---------- ON LOAD: RESTORE OLD FLOW BEHAVIOUR ----------
  useEffect(() => {
    const savedCount = sessionStorage.getItem("DailyUsageCapCount");
    const savedAmount = sessionStorage.getItem("DailyUsageCapAmount");
    const savedEnabled = sessionStorage.getItem("isUsageCapEnabled");

    // FIRST TIME USER → no saved values
    if (!savedCount || !savedAmount) {
      setIsEnabled(false);
      setTransactionLimit("5");
      setAmountLimit("5000");
      return;
    }

    // RETURNING USER → always white summary
    setIsEnabled(savedEnabled === "true");
    setTransactionLimit(savedCount);
    setAmountLimit(savedAmount);
  }, []);

  // ---------- ENABLE (matches old handleEnable) ----------
  const handleEnable = () => {
    const initialCount = countFromSession || "5";
    const initialAmount = amountFromSession || "5000";

    setIsEnabled(true);
    setTransactionLimit(initialCount);
    setAmountLimit(initialAmount);

    sessionStorage.setItem("isUsageCapEnabled", "true");
    sessionStorage.setItem("DailyUsageCapCount", initialCount);
    sessionStorage.setItem("DailyUsageCapAmount", initialAmount);
  };

  // Switch: old flow = only enable, no disable
  const handleSwitchChange = () => {
    if (!isEnabled) {
      handleEnable();
    }
  };

  // ---------- SAVE NEW LIMITS (old handleSaveNewLimits, but in new UI) ----------
  const handleSaveNewLimits = async () => {
    const mobilenumber = JSON.parse(
      sessionStorage.getItem("mobileNumber") || "null",
    );
    const sessionid = sessionStorage.getItem("SessionID");

    const payload = {
      ApplicationType,
      EntityID: entity_id,
      DailyUsageCapAmount: newAmountLimit,
      DailyUsageCapCount: newTransactionLimit,
      Email: "",
      GeoLocation: location,
      MachineID: ip,
      Mobilenumber: mobilenumber,
      Platform,
      NewTPIN: "1234",
      ReqType: "MT_User_UpdateProfile",
      SessionID: sessionid,
      isUsageCapEnabled: true,
    };

    try {
      const res = await api.post(`${baseapi}/MtUserUpdateProfile`, payload);
      const info = res.data.Info?.[0];

      if (res.data?.Response_Code === "1000" && info) {
        const updatedTransactionLimit =
          info?.DailyUsageCapCount?.toString() || newTransactionLimit;
        const updatedAmountLimit =
          info?.DailyUsageCapAmount?.toString() || newAmountLimit;

        // Persist + update UI
        sessionStorage.setItem("DailyUsageCapAmount", updatedAmountLimit);
        sessionStorage.setItem("DailyUsageCapCount", updatedTransactionLimit);
        sessionStorage.setItem("isUsageCapEnabled", "true");

        setAmountLimit(updatedAmountLimit);
        setTransactionLimit(updatedTransactionLimit);
        setIsEnabled(true);
        setShowUpdateFields(false);
        setNewTransactionLimit("");
        setNewAmountLimit("");

        setDialog({
          open: true,
          title: "Success",
          message: "Limits updated successfully!",
          isSuccess: true,
        });
      } else {
        setDialog({
          open: true,
          title: "Failed",
          message: res.data?.Message || "Failed to update usage limits.",
          isSuccess: false,
        });
      }
    } catch (error) {
      console.error("Update error:", error);
      setDialog({
        open: true,
        title: "Error",
        message: "Something went wrong.",
        isSuccess: false,
      });
    }
  };

  const handleBack = () => navigate("/profile-settings");

  return (
    <>
      <DashboardHeader />

      {/* BODY WRAPPER (new design layout) */}
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: pageBg,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          pt: 6,
          pb: 10,
        }}
      >
        <Card
          sx={{
            width: { xs: "92%", sm: "70%", md: "55%", lg: "45%" },
            backgroundColor: cardBg,
            borderRadius: 4,
            p: 4,
            border: borderColor,
            boxShadow: isDark
              ? "0px 8px 30px rgba(0,0,0,0.45)"
              : "0px 4px 16px rgba(0,0,0,0.12)",
          }}
        >
          {/* TITLE + TOGGLE (with lock icon from old flow) */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <IconButton
                onClick={handleBack}
                sx={{
                  bgcolor: isDark ? "#1c2738" : "white",
                  color: "#1e5fd8",
                  border: isDark ? "1px solid #31405a" : "1px solid #d3d7dd",
                }}
              >
                <ArrowBackIosNewOutlined />
              </IconButton>
              <Typography fontSize={22} fontWeight="700" color={textColor}>
                Wallet Usage Limits
              </Typography>
            </Box>

            <Box display="flex" alignItems="center">
              <Typography color={labelColor} sx={{ mr: 1 }}>
                Enable Limits
              </Typography>
              <Switch checked={isEnabled} onChange={handleSwitchChange} />
            </Box>
          </Box>

          {/* =========== STATE 1 — NOT ENABLED YET (FIRST TIME) =========== */}
          {!isEnabled && (
            <Box
              sx={{
                background: innerCardBg,
                p: 3,
                borderRadius: 3,
                border: "1px solid rgba(255,0,0,0.3)",
              }}
            >
              <Typography sx={{ color: "red", fontWeight: 600, mb: 1 }}>
                Note: if usage limits are not enabled, below default limits will
                be set.
              </Typography>

              <Typography sx={{ color: "red", fontSize: 15 }}>
                • Maximum transactions per day: <b>5</b>
              </Typography>

              <Typography sx={{ color: "red", fontSize: 15 }}>
                • Maximum amount per day: <b>Rs.5000</b>
              </Typography>
            </Box>
          )}

          {/* =========== STATE 2 — ENABLED + SUMMARY VIEW (OPTION A) =========== */}
          {isEnabled && !showUpdateFields && (
            <Box
              sx={{
                mt: 3,
                background: innerCardBg,
                p: 3,
                borderRadius: 3,
              }}
            >
              <Typography color={labelColor} mb={0.5}>
                Enter maximum transactions per day :{" "}
                <span style={{ color: textColor, fontWeight: 600 }}>
                  {transactionLimit}
                </span>
              </Typography>

              <Typography color={labelColor} mt={2} mb={0.5}>
                Enter maximum amount per day :{" "}
                <span style={{ color: textColor, fontWeight: 600 }}>
                  Rs.{amountLimit}
                </span>
              </Typography>

              <Box mt={3} display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  onClick={() => setShowUpdateFields(true)}
                  sx={{
                    background: "#2eea67ff",
                    fontWeight: "bold",
                    px: 5,
                    py: 1.2,
                  }}
                >
                  UPDATE
                </Button>
              </Box>
            </Box>
          )}

          {/* =========== STATE 3 — ENABLED + EDIT MODE (TEXTFIELDS) =========== */}
          {isEnabled && showUpdateFields && (
            <Box
              sx={{
                mt: 3,
                background: innerCardBg,
                p: 3,
                borderRadius: 3,
              }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography color={labelColor} mb={1}>
                  Enter maximum transactions per day
                </Typography>
                <TextField
                  fullWidth
                  type="text"
                  placeholder="e.g. 100"
                  value={newTransactionLimit}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "");
                    if (val.length <= 3) setNewTransactionLimit(val);
                  }}
                  inputProps={{ maxLength: 3 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {newTransactionLimit.length}/3
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    input: { color: textColor },
                    background: isDark ? "#0A1524" : "#f3f5f7",
                    borderRadius: 1,
                  }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography color={labelColor} mb={1}>
                  Enter maximum amount per day
                </Typography>
                <TextField
                  fullWidth
                  type="text"
                  placeholder="e.g. 50000"
                  value={newAmountLimit}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "");
                    if (val.length <= 6) setNewAmountLimit(val);
                  }}
                  inputProps={{ maxLength: 6 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {newAmountLimit.length}/6
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    input: { color: textColor },
                    background: isDark ? "#0A1524" : "#f3f5f7",
                    borderRadius: 1,
                  }}
                />
              </Box>

              <Box display="flex" gap={2} mt={2}>
                <Button
                  variant="contained"
                  onClick={handleSaveNewLimits}
                  sx={{
                    flex: 1,
                    background: "#2eea67ff",
                    fontWeight: "bold",
                    py: 1.2,
                  }}
                >
                  SAVE
                </Button>

                <Button
                  variant="outlined"
                  onClick={() => setShowUpdateFields(false)}
                  sx={{
                    flex: 1,
                    borderColor: "red",
                    color: "red",
                    fontWeight: "bold",
                    py: 1.2,
                  }}
                >
                  CANCEL
                </Button>
              </Box>
            </Box>
          )}
        </Card>
      </Box>

      {/* DIALOG (old flow) */}
      <Dialog
        open={dialog.open}
        onClose={() => setDialog({ ...dialog, open: false })}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: dialog.isSuccess ? "green" : "red",
          }}
        >
          {dialog.isSuccess ? (
            <CheckCircleIcon sx={{ color: "green" }} />
          ) : (
            <ErrorIcon sx={{ color: "red" }} />
          )}
          {dialog.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{dialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialog({ ...dialog, open: false })}
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
